import { Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
    DragDropContext,
    DragStart,
    DropResult,
    ResponderProvided
} from "react-beautiful-dnd";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { ISessionState } from "../../../../models/session/sessionState";
import { GridRow } from "../../../framework/Grid/GridRow";
import { TaskRepetitionType } from "ui/Scripts/source/models/enums";
import { calculateEndIndex, calculateRows, calculateStartIndex, FormDesignBuilderDroppableId, FormDesignerDataRow, getWebWidthFromClass, IFormDesignerDragging, ITaskTypeFieldPropUpdate } from "source/components/task/TaskTypeDialog/FormDesignerTypes";
import { AppReportDesignerComponentPicker, componentDefaultValues } from "source/components/task/TaskTypeDialog/TaskTypeDialogFormDesigner/FormDesignerComponentPicker";
import { FormDesignerComponentProperties } from "source/components/task/TaskTypeDialog/TaskTypeDialogFormDesigner/FormDesignerComponentProperties";
import { ReportDesignerBuilder } from "./ReportDesignerBuilder";
import { IAppReportTemplateFormModel } from "ui/Scripts/source/models/appReportTemplate/appReportTemplateFormModel";
import { DocumentFolders } from "source/models/document/documentFolders";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IdColorTitle, IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { IRecordCompanyFurtherEducationGroups } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordCompanyFurtherEducationGroups";

interface IAppReportTemplateDialogFormDesignerProps {
    companyId: string;
    appReportTemplate: IAppReportTemplateFormModel;
    formDataRow?: ITaskDataRow;
    formTypeCol: ITaskTypeCol;
    sessionState: ISessionState;
    //Event reference
    eventReports?: IIdTitleNode[];
    eventUnits?: IIdTitleNode[];
    eventStates?: IIdTitle[];
    eventUrgencyClasses?: IdColorTitle[];
    eventTypes?: IIdTitleNode[];
    eventTypeBs?: IIdTitleNode[];
    eventTypeCs?: IIdTitleNode[];
    //Task reference
    taskTypes?: ITaskEditTypeItem[];
    taskUnits?: IIdTitleNode[];
    taskStates?: IIdColorTitle[];
    //RecordReference related lists
    recordCharts?: IIdTitle[];
    recordUnits?: IIdTitleNode[];
    recordFurtherEducationGroups?: IRecordCompanyFurtherEducationGroups[];
    onFormSchemaReorder: (startIndex: number, endIndex: number) => void;
    onFormSchemaAddItem: (fieldType: string, index: number) => void;
    onFormItemClick: (id: string) => void;
    onTaskTypeFormPropUpdate: (values: ITaskTypeFieldPropUpdate[]) => void;
    onFormTaskDataRowValueUpdate: (id: string, name: string, value: string) => void;
    onFormRemoveComponent: (id: string) => void;
    onFormCopyComponent: (id: string) => void;
    onViewSchema?: () => void;
    onGetDocumentFoldersAndDocuments?: (companyId: string) => Promise<DocumentFolders>;
}

interface IAppReportTemplateDialogFormDesignerState {
    dragging?: IFormDesignerDragging,
    formDesignerRows?: FormDesignerDataRow[];
    propertiesExpanded: boolean;
}

export const AppReportTemplateDialogFormDesigner = (props: IAppReportTemplateDialogFormDesignerProps) => {
    const [state, setState] = useState<IAppReportTemplateDialogFormDesignerState>({ propertiesExpanded: false });
    useEffect(() => {
        setState((old) => ({
            ...old,
            formDesignerRows: calculateRows(props.appReportTemplate?.formCols, FormDesignBuilderDroppableId.Form)
        }));
    }, [props.appReportTemplate?.formCols]);

    const handleDragEnd = (result: DropResult, provided: ResponderProvided) => {
        //console.log("handleDragEnd S", result?.destination, result?.source, `'${result?.draggableId}'`);
        if (!result?.destination) return;
        if (result?.source?.droppableId === FormDesignBuilderDroppableId.Picker) {
            setTimeout(() => {
                if ((result?.destination.droppableId ?? "").startsWith(FormDesignBuilderDroppableId.Form)) {
                    const startIndex = calculateStartIndex(props.appReportTemplate.formCols, result.draggableId);
                    const endIndex = calculateEndIndex(state.formDesignerRows, result.source.droppableId, result.destination.droppableId, result.destination.index, startIndex);
                    props.onFormSchemaAddItem(result.draggableId, endIndex);
                }
            }, 10);
        } else if ((result?.source?.droppableId ?? "").startsWith(FormDesignBuilderDroppableId.Form)) {
            const field = result.draggableId?.substring(FormDesignBuilderDroppableId.Form.length) ?? "";
            const startIndex = calculateStartIndex(props.appReportTemplate.formCols, field);
            const endIndex = calculateEndIndex(state.formDesignerRows, result.source.droppableId, result.destination.droppableId, result.destination.index, startIndex);
            props.onFormSchemaReorder(startIndex, endIndex);
        }
        setState((old) => ({
            ...old,
            dragging: null,
        }));
    };

    const handleDragStart = (initial: DragStart) => {
        //console.log("handleDragStart S", initial, initial.source, initial.source?.droppableId, initial.draggableId, props.taskType);
        if (!initial.source || !initial.draggableId || !props.appReportTemplate) return;
        const droppableId = initial.source.droppableId;
        let width = 6;
        if (droppableId === FormDesignBuilderDroppableId.Picker) {
            width = getWebWidthFromClass(componentDefaultValues[initial.draggableId]?.defaultProps?.class) || 6;
        } else {
            let taskTypeCols: ITaskTypeCol[] = [];
            let field = "";
            if (droppableId.startsWith(FormDesignBuilderDroppableId.Form)) {
                taskTypeCols = props.appReportTemplate.formCols;
                field = initial.draggableId.substring(FormDesignBuilderDroppableId.Form.length) ?? "";
            } else {
                return;
            }
            const taskTypeCol = taskTypeCols.find(item => item.field === field);
            if (!taskTypeCol) return;
            width = getWebWidthFromClass(taskTypeCol.class);
        }
        //console.log("handleDragStart M", width, droppableId);
        setState((old) => ({
            ...old,
            dragging: {
                width,
                source: droppableId,
            },
        }));
    };

    const handleToggleExpand = () => {
        setState((old) => ({
            ...old,
            propertiesExpanded: !old.propertiesExpanded,
        }));
    };

    return (
        <GridRow sx={{ flexGrow: 1 }}>
            <DragDropContext
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
            >
                {!state.propertiesExpanded &&
                    <Grid item sm={3}>
                        <AppReportDesignerComponentPicker
                            companyId={props.companyId}
                        />
                    </Grid>
                }
                <Grid item sm={6}>
                    <ReportDesignerBuilder
                        appReportTemplate={props.appReportTemplate}
                        formDesignerRows={state?.formDesignerRows}
                        formDataRow={props.formDataRow}
                        formTypeCol={props.formTypeCol}
                        sessionState={props.sessionState}
                        draggingWidth={state?.dragging?.width}
                        draggingSource={state?.dragging?.source}
                        onFormItemClick={props.onFormItemClick}
                        onFormRemoveComponent={props.onFormRemoveComponent}
                        onFormCopyComponent={props.onFormCopyComponent}
                        onViewSchema={props.onViewSchema}
                    />
                </Grid>
            </DragDropContext>
            <Grid item sm={state.propertiesExpanded ? 6 : 3}>
                <FormDesignerComponentProperties
                    companyId={props.companyId}
                    taskRepetitionType={TaskRepetitionType.NotRepeated}
                    headerCols={[]}
                    formCols={props.appReportTemplate.formCols}
                    lineCols={[]}
                    headerTypeCol={null}
                    formTypeCol={props.formTypeCol}
                    lineTypeCol={null}
                    lineColumnWidths={[]}
                    formDataCol={props.formDataRow && props.formTypeCol
                        ? props.formDataRow.cols.find(c => c.field === props.formTypeCol.field)
                        : null}
                    appReportDesignMode={true}
                    expanded={state.propertiesExpanded}
                    eventReports={props.eventReports}
                    eventUnits={props.eventUnits}
                    eventStates={props.eventStates}
                    eventUrgencyClasses={props.eventUrgencyClasses}
                    eventTypes={props.eventTypes}
                    eventTypeBs={props.eventTypeBs}
                    eventTypeCs={props.eventTypeCs}
                    taskTypes={props.taskTypes}
                    taskUnits={props.taskUnits}
                    taskStates={props.taskStates}
                    recordCharts={props.recordCharts}
                    recordUnits={props.recordUnits}
                    recordFurtherEducationGroups={props.recordFurtherEducationGroups}
                    onHeaderItemClick={null}
                    onFormItemClick={props.onFormItemClick}
                    onTaskTypeHeaderPropUpdate={null}
                    onTaskTypeFormPropUpdate={props.onTaskTypeFormPropUpdate}
                    onTaskTypeLinePropUpdate={null}
                    onHeaderTaskDataRowValueUpdate={null}
                    onFormTaskDataRowValueUpdate={props.onFormTaskDataRowValueUpdate}
                    onAddFieldToLine={null}
                    onRemoveFieldFromLine={null}
                    onChangeLineColumnWidth={null}
                    onToggleExpand={handleToggleExpand}
                    onGetDocumentFoldersAndDocuments={props.onGetDocumentFoldersAndDocuments}
                />
            </Grid>
        </GridRow>
    );
};
